import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface LeadRecord {
  id: string;
  userId?: string;
  claimId?: string;
  leadType?: string;
  createdAt?: string;
  status?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  addressLine1?: string;
  city?: string;
  postcode?: string;
  dateOfBirth?: string;
  agreement_dateAgreed?: string;
  damageBasedAgreementUrl?: string;
  formOfAuthorityUrl?: string;
  creditcheckUrl?: string;
  amlcheckUrl?: string;
  cancelledAt?: string;
  claimsAlreadyTransferred?: string[];
}

export interface LeadsListResponse {
  leads: LeadRecord[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

export interface LeadCreditCheckSummary {
  leadId: string;
  provider?: string;
  formatType?: string;
  summary: any;
  applicant: any;
  creditAccounts: any[];
  electoralRoll: any;
  reportAvailable: boolean;
}

export interface CancelLeadResponse {
  success: boolean;
  message?: string;
  error?: string;
  claimsAlreadyTransferred?: string[];
}

export interface LeadAmlSummary {
  leadId: string;
  summary: {
    status?: string;
    identityResult?: string | null;
    identityScore?: string | null;
    matchLevel?: string | null;
    numPrimaryChecks?: string | null;
    numCorroborativeChecks?: string | null;
    warnings: Array<{ label: string; value: string }>;
    input: any;
    raw: any;
  };
  reportAvailable: boolean;
}

export const leadsApi = {
  async listLeads(params: {
    searchTerm?: string;
    status?: string;
    leadType?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<LeadsListResponse> {
    const queryParams = new URLSearchParams();

    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.status) queryParams.append('status', params.status);
    if (params.leadType) queryParams.append('leadType', params.leadType);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await axiosInstance.get<LeadsListResponse>(`/ui/leads/list?${queryParams.toString()}`);
    return response.data;
  },

  async getCreditCheckSummary(leadId: string): Promise<LeadCreditCheckSummary> {
    const response = await axiosInstance.get<LeadCreditCheckSummary>(`/ui/leads/${leadId}/credit-check-summary`);
    return response.data;
  },

  async getAmlSummary(leadId: string): Promise<LeadAmlSummary> {
    const response = await axiosInstance.get<LeadAmlSummary>(`/ui/leads/${leadId}/aml-summary`);
    return response.data;
  },

  async cancelLead(leadId: string): Promise<CancelLeadResponse> {
    const response = await axiosInstance.post<CancelLeadResponse>(`/ui/leads/${encodeURIComponent(leadId)}/cancel`, {});
    return response.data;
  },
};
